import clsx from 'clsx';
import React, { useEffect, useMemo, useRef } from 'react';

type YTPlayer = {
  destroy(): void;
  getCurrentTime(): number;
  getDuration(): number;
};

type YTReadyEvent = { target: YTPlayer };
type YTStateEvent = YTReadyEvent & { data: number };
type TimeUpdatePayload = { playedPercentage: number; currentTime: number; duration: number };
type PlaybackEvent = { playedPercentage: number };

type YouTubeIframeProps = {
  videoId?: string;
  src?: string;
  className?: string;
  aspectClassName?: string;
  onReady?: (event: YTReadyEvent) => void;
  onPlay?: (event: PlaybackEvent) => void;
  onPause?: (event: PlaybackEvent) => void;
  onEnd?: (event: YTStateEvent) => void;
  onStateChange?: (event: YTStateEvent) => void;
  onTimeUpdate?: (payload: TimeUpdatePayload) => void;
  playerVars?: Record<string, string | number>;
};

type YouTubeNamespace = {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (event: YTReadyEvent) => void;
        onStateChange?: (event: YTStateEvent) => void;
      };
    }
  ) => YTPlayer;
  PlayerState: {
    ENDED: number;
    PAUSED: number;
    PLAYING: number;
  };
};

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const PLAYER_STATE = {
  ENDED: 0,
  PAUSED: 2,
  PLAYING: 1,
} as const;

const YT_API_SRC = 'https://www.youtube.com/iframe_api';
let ytApiPromise: Promise<YouTubeNamespace> | null = null;

function loadYouTubeIframeApi() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube API requires a browser environment'));
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve, reject) => {
      const onReady = () => window.YT?.Player && resolve(window.YT);
      const onError = () => reject(new Error('Failed to load YouTube IFrame API'));

      const existingScript = document.querySelector(
        `script[src="${YT_API_SRC}"]`
      ) as HTMLScriptElement | null;

      if (existingScript) {
        window.onYouTubeIframeAPIReady = onReady;
        existingScript.addEventListener('error', onError, { once: true });
        return;
      }

      const tag = document.createElement('script');
      tag.src = YT_API_SRC;
      tag.async = true;
      tag.onerror = onError;
      window.onYouTubeIframeAPIReady = onReady;
      document.head.appendChild(tag);
    });
  }

  return ytApiPromise;
}

export function extractVideoId(src?: string) {
  if (!src) return '';
  const match = src.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{6,})/);
  return match?.[1] ?? '';
}

function calculatePlayedPercentage(player: YTPlayer) {
  const duration = player?.getDuration?.() || 0;

  if (!duration) return { currentTime: 0, duration: 0, playedPercentage: 0 };

  const currentTime = player?.getCurrentTime?.() || 0;
  const playedPercentage = Math.ceil((currentTime / duration) * 100);

  return { currentTime, duration, playedPercentage };
}

const YouTubeIframe = ({
  videoId,
  src,
  className,
  aspectClassName,
  onReady,
  onPlay,
  onPause,
  onEnd,
  onStateChange,
  onTimeUpdate,
  playerVars,
}: YouTubeIframeProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const intervalRef = useRef<number>();
  const seenPercentagesRef = useRef<Set<number>>(new Set());
  const callbacksRef = useRef({
    onReady,
    onPlay,
    onPause,
    onEnd,
    onStateChange,
    onTimeUpdate,
  });

  callbacksRef.current = { onReady, onPlay, onPause, onEnd, onStateChange, onTimeUpdate };

  const resolvedVideoId = useMemo(() => videoId || extractVideoId(src), [videoId, src]);

  const mergedPlayerVars = useMemo(
    () => ({
      enablejsapi: 1,
      modestbranding: 1,
      origin: typeof window !== 'undefined' ? window.location.origin : undefined,
      playsinline: 1,
      rel: 0,
      ...playerVars,
    }),
    [playerVars]
  );

  useEffect(() => {
    if (!resolvedVideoId || typeof window === 'undefined' || !containerRef.current) return;

    let player: YTPlayer | null = null;
    let cancelled = false;

    seenPercentagesRef.current = new Set();

    const stopPolling = () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };

    const startPolling = () => {
      stopPolling();
      intervalRef.current = window.setInterval(() => {
        const { playedPercentage, currentTime, duration } = calculatePlayedPercentage(player);
        if (
          [25, 50, 75, 100].includes(playedPercentage) &&
          !seenPercentagesRef.current.has(playedPercentage)
        ) {
          seenPercentagesRef.current.add(playedPercentage);
          callbacksRef.current.onTimeUpdate?.({ playedPercentage, currentTime, duration });
        }
      }, 500);
    };

    loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled || !containerRef.current) return;

        player = new YT.Player(containerRef.current, {
          videoId: resolvedVideoId,
          playerVars: mergedPlayerVars,
          events: {
            onReady: (event) => callbacksRef.current.onReady?.(event),
            onStateChange: (event) => {
              callbacksRef.current.onStateChange?.(event);
              const { playedPercentage } = calculatePlayedPercentage(player);

              switch (event.data) {
                case PLAYER_STATE.PLAYING:
                  callbacksRef.current.onPlay?.({ playedPercentage });
                  startPolling();
                  break;
                case PLAYER_STATE.PAUSED:
                  callbacksRef.current.onPause?.({
                    playedPercentage,
                  });
                  stopPolling();
                  break;
                case PLAYER_STATE.ENDED:
                  callbacksRef.current.onEnd?.(event);
                  stopPolling();
                  break;
                default:
                  break;
              }
            },
          },
        });
      })
      .catch(() => {
        /* ignore load errors to keep component resilient */
      });

    return () => {
      cancelled = true;
      stopPolling();
      player?.destroy();
    };
  }, [mergedPlayerVars, resolvedVideoId]);

  if (!resolvedVideoId) return null;

  return (
    <div
      className={clsx(
        'relative h-auto w-full overflow-hidden',
        aspectClassName || 'aspect-[16/9]',
        className
      )}
    >
      <div ref={containerRef} className="absolute inset-0" data-testid="youtube-iframe" />
    </div>
  );
};

export default YouTubeIframe;
